import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Store, Building2, Mail, Lock, ArrowRight, AlertCircle,
  Eye, EyeOff, Users, ShieldCheck, Activity,
  Zap, Globe, TrendingUp, Database, Server
} from "lucide-react";
import { loginReseller, loginMerchant, loginStaff } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

function LiveClock({ color }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(i); }, []);
  return <span style={{ fontFamily:"'Geist Mono',monospace", fontSize:11, color, letterSpacing:"0.1em" }}>{time.toLocaleTimeString("en-US",{hour12:false})}</span>;
}

function CyberGrid({ color }) {
  return <div style={{ position:"absolute", inset:0, pointerEvents:"none", backgroundImage:`linear-gradient(${color} 1px,transparent 1px),linear-gradient(90deg,${color} 1px,transparent 1px)`, backgroundSize:"48px 48px", maskImage:"radial-gradient(ellipse 90% 90% at 50% 50%,black 20%,transparent 100%)", WebkitMaskImage:"radial-gradient(ellipse 90% 90% at 50% 50%,black 20%,transparent 100%)" }} />;
}

function ScanLine({ color }) {
  return (
    <motion.div
      style={{ position:"absolute", left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${color}55,${color}99,${color}55,transparent)`, filter:"blur(1px)", pointerEvents:"none", zIndex:2 }}
      animate={{ top:["0%","100%"] }}
      transition={{ duration:5, repeat:Infinity, ease:"linear", repeatDelay:3 }}
    />
  );
}

function DataNodes({ color }) {
  const nodes = useRef(Array.from({length:10},(_,i)=>({ id:i, x:10+Math.random()*80, y:10+Math.random()*80, size:Math.random()*4+2, duration:Math.random()*10+8, delay:Math.random()*6 }))).current;
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
      {nodes.map(n=>(
        <motion.div key={n.id} style={{ position:"absolute", left:`${n.x}%`, top:`${n.y}%`, width:n.size, height:n.size, borderRadius:"50%", background:color, boxShadow:`0 0 6px ${color}` }}
          animate={{ opacity:[0,0.7,0], scale:[0.5,1.3,0.5] }}
          transition={{ duration:n.duration, delay:n.delay, repeat:Infinity }} />
      ))}
    </div>
  );
}

function LoadingStages({ stage, accent }) {
  const stages = ["Connecting","Authenticating","Authorizing","Granting Access"];
  return (
    <div style={{ width:"100%", marginBottom:20 }}>
      <div style={{ position:"relative", height:3, background:`${accent}18`, borderRadius:3, overflow:"hidden" }}>
        <motion.div style={{ height:"100%", background:`linear-gradient(90deg,${accent}99,${accent},${accent}cc)`, borderRadius:3 }} animate={{ width:`${(stage/stages.length)*100}%` }} transition={{ duration:0.6, ease:"easeOut" }} />
        <motion.div style={{ position:"absolute", top:0, left:0, bottom:0, width:"40%", background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.32),transparent)" }}
          animate={{ x:["-100%","300%"] }} transition={{ duration:1.1, repeat:stage>0&&stage<4?Infinity:0, ease:"easeInOut" }} />
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:8 }}>
        {stages.map((s,i)=>(
          <motion.div key={s} style={{ width:7, height:7, borderRadius:"50%", background:i<stage?accent:`${accent}22`, boxShadow:i<stage?`0 0 10px ${accent}`:"none" }}
            animate={i<stage?{scale:[1,1.5,1]}:{}} transition={{ duration:0.5, repeat:i===stage-1?Infinity:0 }} />
        ))}
      </div>
      <motion.p key={stage} initial={{ opacity:0,y:4 }} animate={{ opacity:1,y:0 }}
        style={{ fontFamily:"'Geist Mono',monospace", fontSize:10, color:`${accent}88`, marginTop:6, letterSpacing:"0.08em" }}>
        {stage>0?`> ${stages[Math.min(stage-1,3)]}...`:"> Idle"}
      </motion.p>
    </div>
  );
}

const TABS = {
  merchant: {
    key:"merchant", label:"Merchant", fullLabel:"Merchants", icon:Building2,
    accent:"#10B981", accentB:"#34D399", accentC:"#6EE7B7",
    border:"rgba(16,185,129,0.2)", glow:"rgba(16,185,129,0.35)", glowSoft:"rgba(16,185,129,0.1)", dimBg:"rgba(16,185,129,0.07)",
    gradient:"linear-gradient(135deg,#064E3B 0%,#059669 50%,#10B981 100%)",
    shadow:"rgba(5,150,105,0.4)",
    inputBg:"rgba(2,18,10,0.88)", inputFocus:"rgba(2,26,14,0.96)",
    textColor:"#ECFDF5", subText:"rgba(110,231,183,0.55)", labelColor:"rgba(52,211,153,0.7)", linkColor:"#34D399",
    gridColor:"rgba(16,185,129,0.03)", nodeColor:"rgba(16,185,129,0.6)",
    cardBg:"linear-gradient(150deg,rgba(4,20,12,0.97) 0%,rgba(2,12,7,0.99) 100%)",
    placeholder:"merchant@company.com",
    desc:"Business Payment Management",
    tagline:"Power your payments with total confidence",
    features:[
      {icon:Zap,       text:"Real-time transaction processing"},
      {icon:ShieldCheck,text:"Bank-grade security & encryption"},
      {icon:TrendingUp,text:"Advanced analytics dashboard"},
      {icon:Globe,     text:"Multi-currency support"},
      {icon:Database,  text:"Automated reconciliation"},
      {icon:Server,    text:"99.99% uptime guarantee"},
    ],
  },
  reseller: {
    key:"reseller", label:"Reseller", fullLabel:"Resellers", icon:Store,
    accent:"#F97316", accentB:"#FB923C", accentC:"#FCD34D",
    border:"rgba(249,115,22,0.2)", glow:"rgba(249,115,22,0.35)", glowSoft:"rgba(249,115,22,0.1)", dimBg:"rgba(249,115,22,0.07)",
    gradient:"linear-gradient(135deg,#7C2D12 0%,#C2410C 50%,#F97316 100%)",
    shadow:"rgba(234,88,12,0.4)",
    inputBg:"rgba(18,7,2,0.88)", inputFocus:"rgba(26,9,2,0.96)",
    textColor:"#FFF7ED", subText:"rgba(253,186,116,0.55)", labelColor:"rgba(251,146,60,0.7)", linkColor:"#FB923C",
    gridColor:"rgba(249,115,22,0.03)", nodeColor:"rgba(249,115,22,0.6)",
    cardBg:"linear-gradient(150deg,rgba(22,10,2,0.97) 0%,rgba(14,6,1,0.99) 100%)",
    placeholder:"reseller@network.com",
    desc:"Partner & Reseller Network",
    tagline:"Scale your business across our global network",
    features:[
      {icon:Globe,     text:"Global partner network access"},
      {icon:TrendingUp,text:"Commission tracking & reports"},
      {icon:ShieldCheck,text:"Dedicated partner support"},
      {icon:Zap,       text:"Instant settlement processing"},
      {icon:Database,  text:"Sub-reseller management"},
      {icon:Server,    text:"White-label payment tools"},
    ],
  },
};

export default function PortalLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("merchant");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [stage,    setStage]    = useState(0);
  const [success,  setSuccess]  = useState(false);
  const [error,    setError]    = useState("");
  const [focused,  setFocused]  = useState("");
  const [isStaff,  setIsStaff]  = useState(false);
  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x:50, y:50 });

  const tab = TABS[activeTab];

  useEffect(() => {
    const h = (e) => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setMouse({ x:((e.clientX-r.left)/r.width)*100, y:((e.clientY-r.top)/r.height)*100 });
    };
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, []);

  const switchTab = (val) => {
    setActiveTab(val); setError(""); setEmail(""); setPassword("");
    setStage(0); setSuccess(false); setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email||!password) { setError(t("pleaseEnterBothEmailAndPassword")); return; }
    setLoading(true);
    const advance = (s) => new Promise(res => setTimeout(()=>{ setStage(s); res(); }, 640));
    try {
      setStage(1); await advance(2);
      if (isStaff) { await loginStaff(email, password, activeTab==="reseller"?"RESELLER":"MERCHANT"); }
      else { activeTab==="reseller" ? await loginReseller(email,password) : await loginMerchant(email,password); }
      await advance(3); await advance(4);
      setSuccess(true);
      setTimeout(() => navigate(activeTab==="reseller"?"/ResellerDashboard":"/MerchantDashboard"), 1100);
    } catch (err) {
      setError(err.message||t("loginFailed")); setLoading(false); setStage(0);
    }
  };

  const TabIcon = tab.icon;

  return (
    <div ref={containerRef} style={{ minHeight:"100vh", background:"#040812", display:"flex", flexDirection:"column", fontFamily:"'Outfit',sans-serif", position:"relative", overflow:"hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Playfair+Display:wght@700;800&display=swap');
        *{box-sizing:border-box;}

        .pl3-tab{flex:1;height:42px;border:none;border-radius:11px;font-family:'Outfit',sans-serif;font-size:13px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:7px;position:relative;overflow:hidden;letter-spacing:0.01em;transition:all 0.28s cubic-bezier(0.4,0,0.2,1);}
        .pl3-tab-sh{position:absolute;inset:0;background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,0.08) 50%,transparent 65%);transform:translateX(-100%);transition:transform 0.55s ease;}
        .pl3-tab:hover .pl3-tab-sh{transform:translateX(100%);}

        .pl3-inp{width:100%;height:50px;border-radius:13px;font-family:'Outfit',sans-serif;font-size:14px;font-weight:500;padding:0 48px;outline:none;letter-spacing:0.01em;transition:all 0.28s cubic-bezier(0.4,0,0.2,1);-webkit-appearance:none;}

        .pl3-btn{width:100%;height:50px;border:none;border-radius:13px;color:white;font-family:'Outfit',sans-serif;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:9px;position:relative;overflow:hidden;letter-spacing:0.02em;transition:all 0.3s cubic-bezier(0.4,0,0.2,1);}
        .pl3-btn-sh{position:absolute;inset:0;background:linear-gradient(105deg,transparent 35%,rgba(255,255,255,0.14) 50%,transparent 65%);transform:translateX(-100%);transition:transform 0.6s ease;}
        .pl3-btn:not(:disabled):hover .pl3-btn-sh{transform:translateX(100%);}
        .pl3-btn:not(:disabled):hover{transform:translateY(-2px);}
        .pl3-btn:not(:disabled):active{transform:translateY(0) scale(0.99);}
        .pl3-btn:disabled{opacity:0.6;cursor:not-allowed;}

        .pl3-spin{width:18px;height:18px;border:2.5px solid rgba(255,255,255,0.28);border-top-color:white;border-radius:50%;animation:pl3rot 0.7s linear infinite;}
        @keyframes pl3rot{to{transform:rotate(360deg);}}

        .pl3-feat{display:flex;align-items:center;gap:13px;padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.04);transition:transform 0.2s;cursor:default;}
        .pl3-feat:last-child{border-bottom:none;}
        .pl3-feat:hover{transform:translateX(5px);}

        .pl3-staff{display:flex;align-items:center;gap:10px;padding:11px 14px;border-radius:12px;cursor:pointer;transition:all 0.22s ease;}

        /* Autofill override */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 9999px #030c06 inset !important;
          box-shadow: 0 0 0 9999px #030c06 inset !important;
          -webkit-text-fill-color: #d1fae5 !important;
          transition: background-color 99999s ease-in-out 0s !important;
          background-color: transparent !important;
        }

        /* ── Layout ── */
        .pl3-statusbar {
          position: fixed; top: 0; left: 0; right: 0; height: 44px;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 20px;
          background: rgba(4,8,18,0.82); backdrop-filter: blur(24px);
          border-bottom: 1px solid; z-index: 50;
          transition: border-color 0.4s ease;
        }

        .pl3-main {
          display: flex;
          width: 100%;
          max-width: 1180px;
          margin: 0 auto;
          align-items: center;
          gap: 64px;
          padding: 44px 60px 60px;
          position: relative;
          z-index: 1;
          min-height: 100vh;
          flex: 1;
        }

        .pl3-left { flex: 1; }
        .pl3-right { width: 440px; flex-shrink: 0; }

        .pl3-card {
          position: relative; overflow: hidden;
          transition: background 0.5s ease, border-color 0.4s ease, box-shadow 0.5s ease;
        }

        /* ── Tablet ── */
        @media (max-width: 1024px) {
          .pl3-main { gap: 40px; padding: 44px 32px 60px; }
          .pl3-right { width: 400px; }
        }

        /* ── Mobile landscape / small tablet ── */
        @media (max-width: 860px) {
          .pl3-left { display: none !important; }
          .pl3-main {
            justify-content: center;
            padding: 54px 20px 40px;
            min-height: 100vh;
            align-items: flex-start;
          }
          .pl3-right {
            width: 100%;
            max-width: 480px;
            margin: 0 auto;
          }
        }

        /* ── Mobile ── */
        @media (max-width: 520px) {
          .pl3-statusbar { padding: 0 14px; }
          .pl3-statusbar .pl3-status-right { display: none; }
          .pl3-main { padding: 52px 14px 32px; align-items: flex-start; }
          .pl3-right { max-width: 100%; }
          .pl3-card { padding: 24px 18px !important; border-radius: 18px !important; }
          .pl3-tab { font-size: 12px; gap: 5px; height: 38px; }
          .pl3-tab svg { width: 13px; height: 13px; }
          .pl3-inp { height: 46px; font-size: 13px; }
          .pl3-btn { height: 46px; font-size: 13px; }
        }

        /* ── Very small ── */
        @media (max-width: 360px) {
          .pl3-main { padding: 50px 10px 28px; }
          .pl3-card { padding: 20px 14px !important; }
        }
      `}</style>

      {/* Ambient orbs */}
      <div style={{ position:"fixed", width:600, height:600, borderRadius:"50%", background:`radial-gradient(circle,${tab.glowSoft} 0%,transparent 65%)`, top:`${mouse.y*0.14-6}%`, left:`${mouse.x*0.14-6}%`, filter:"blur(70px)", pointerEvents:"none", zIndex:0, transition:"all 1.5s cubic-bezier(0.23,1,0.32,1),background 0.5s ease" }} />
      <div style={{ position:"fixed", width:400, height:400, borderRadius:"50%", background:`radial-gradient(circle,${tab.glowSoft} 0%,transparent 65%)`, bottom:"5%", right:"5%", filter:"blur(80px)", pointerEvents:"none", zIndex:0, transition:"background 0.5s ease" }} />

      {/* Status bar */}
      <motion.div className="pl3-statusbar" initial={{ opacity:0,y:-20 }} animate={{ opacity:1,y:0 }} transition={{ duration:0.5,delay:0.1 }}
        style={{ borderColor: tab.border }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <motion.div style={{ width:7,height:7,borderRadius:"50%",background:"#4ADE80",boxShadow:"0 0 10px #4ADE80" }} animate={{ scale:[1,1.5,1],opacity:[1,0.5,1] }} transition={{ duration:2.2,repeat:Infinity }} />
          <span style={{ fontFamily:"'Geist Mono',monospace",fontSize:10,color:`${tab.accent}55`,letterSpacing:"0.1em",textTransform:"uppercase" }}>System Operational</span>
        </div>
        <div className="pl3-status-right" style={{ display:"flex",alignItems:"center",gap:16 }}>
          <Activity size={11} color={`${tab.accent}44`} />
          <span style={{ fontFamily:"'Geist Mono',monospace",fontSize:10,color:`${tab.accent}33` }}>v4.2.1</span>
          <LiveClock color={`${tab.accent}66`} />
        </div>
      </motion.div>

      {/* Main layout */}
      <div className="pl3-main">

        {/* LEFT PANEL */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab+"-left"} className="pl3-left"
            initial={{ opacity:0,x:-28 }} animate={{ opacity:1,x:0 }} exit={{ opacity:0,x:-20 }}
            transition={{ duration:0.55,ease:[0.22,1,0.36,1] }}>

            {/* Logo */}
            <motion.div style={{ display:"flex",alignItems:"center",gap:14,marginBottom:44 }} initial={{ opacity:0,y:-10 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.18 }}>
              <motion.div whileHover={{ scale:1.1,rotate:-6 }} transition={{ type:"spring",stiffness:400,damping:15 }}
                style={{ width:50,height:50,borderRadius:15,background:tab.gradient,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 8px 30px ${tab.shadow},0 0 0 1px ${tab.border}`,position:"relative",overflow:"hidden" }}>
                <div style={{ position:"absolute",top:0,left:0,right:0,height:"45%",background:"rgba(255,255,255,0.2)",borderRadius:"inherit" }} />
                <TabIcon size={21} color="white" />
              </motion.div>
              <div>
                <div style={{ fontFamily:"'Playfair Display',serif",fontWeight:800,fontSize:22,color:"#F0F9FF",letterSpacing:"-0.02em",lineHeight:1 }}>1401 Portal</div>
                <div style={{ fontFamily:"'Geist Mono',monospace",fontSize:10,color:tab.subText,letterSpacing:"0.14em",marginTop:4,textTransform:"uppercase" }}>{tab.desc}</div>
              </div>
            </motion.div>

            {/* Hero */}
            <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.26 }}>
              <h1 style={{ fontFamily:"'Playfair Display',serif",fontSize:42,fontWeight:800,color:"#F0F9FF",letterSpacing:"-0.03em",lineHeight:1.15,margin:"0 0 12px" }}>
                Welcome to<br />
                <span style={{ background:`linear-gradient(135deg,${tab.accent},${tab.accentB},${tab.accentC})`,WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>
                  {tab.fullLabel}
                </span>
              </h1>
              <p style={{ fontSize:14,color:tab.subText,lineHeight:1.78,margin:"0 0 36px",maxWidth:340,fontWeight:400 }}>{tab.tagline}. Log in to access your dashboard and manage your business.</p>
            </motion.div>

            {/* Features */}
            <div style={{ maxWidth:360 }}>
              {tab.features.map((feat,i)=>(
                <motion.div key={feat.text} className="pl3-feat"
                  initial={{ opacity:0,x:-16 }} animate={{ opacity:1,x:0 }} transition={{ delay:0.35+i*0.07 }}>
                  <div style={{ width:34,height:34,borderRadius:10,flexShrink:0,background:`${tab.accent}16`,border:`1px solid ${tab.border}`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 14px ${tab.glowSoft}` }}>
                    <feat.icon size={15} color={tab.accent} />
                  </div>
                  <span style={{ fontSize:13,color:`${tab.textColor}aa`,fontWeight:500 }}>{feat.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Trust tags */}
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.8 }}
              style={{ display:"flex",gap:7,marginTop:32,flexWrap:"wrap" }}>
              {["PCI DSS","ISO 27001","256-bit SSL","SOC 2 Type II"].map(tag=>(
                <span key={tag} style={{ padding:"3px 10px",borderRadius:6,background:`${tab.accent}0e`,border:`1px solid ${tab.border}`,fontFamily:"'Geist Mono',monospace",fontSize:10,color:tab.subText,fontWeight:600,letterSpacing:"0.06em" }}>{tag}</span>
              ))}
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* RIGHT CARD */}
        <motion.div className="pl3-right"
          initial={{ opacity:0,y:28 }} animate={{ opacity:1,y:0 }}
          transition={{ duration:0.65,ease:[0.22,1,0.36,1],delay:0.1 }}>

          <div className="pl3-card" style={{ background:tab.cardBg,border:`1px solid ${tab.border}`,borderRadius:22,padding:"34px 30px",boxShadow:`0 4px 60px rgba(0,0,0,0.75),0 0 0 1px rgba(255,255,255,0.02),0 0 80px ${tab.glowSoft}` }}>

            <CyberGrid color={tab.gridColor} />
            <ScanLine color={tab.accent} />
            <DataNodes color={tab.nodeColor} />

            {/* Top shimmer */}
            <div style={{ position:"absolute",top:0,left:0,right:0,height:1,background:`linear-gradient(90deg,transparent,${tab.accent}99 35%,${tab.accentB}99 65%,transparent)`,transition:"background 0.4s ease" }} />

            {/* Card header */}
            <div style={{ marginBottom:22,position:"relative",zIndex:3 }}>
              <AnimatePresence mode="wait">
                <motion.div key={activeTab+"-ico"}
                  initial={{ scale:0.6,opacity:0,rotate:-15 }} animate={{ scale:1,opacity:1,rotate:0 }} exit={{ scale:0.6,opacity:0,rotate:15 }}
                  transition={{ type:"spring",stiffness:300,damping:20 }}
                  whileHover={{ scale:1.07,rotate:5 }}
                  style={{ width:50,height:50,borderRadius:14,background:tab.gradient,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16,boxShadow:`0 8px 32px ${tab.shadow},0 0 0 1px ${tab.border}`,position:"relative",overflow:"hidden",cursor:"default" }}>
                  <div style={{ position:"absolute",top:0,left:0,right:0,height:"45%",background:"rgba(255,255,255,0.2)",borderRadius:"inherit" }} />
                  <TabIcon size={22} color="white" />
                </motion.div>
              </AnimatePresence>
              <AnimatePresence mode="wait">
                <motion.div key={activeTab+"-title"} initial={{ opacity:0,y:4 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0 }}>
                  <h2 style={{ fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:800,color:tab.textColor,margin:0,letterSpacing:"-0.02em" }}>{tab.label} Sign In</h2>
                  <p style={{ fontFamily:"'Outfit',sans-serif",fontSize:13,color:tab.subText,margin:"4px 0 0",fontWeight:400 }}>{tab.desc} · Authorized access only</p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Tab switcher */}
            <div style={{ display:"flex",gap:5,padding:5,background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,marginBottom:20,position:"relative",zIndex:3 }}>
              {Object.values(TABS).map(cfg=>{
                const active = activeTab===cfg.key;
                const Icon = cfg.icon;
                return (
                  <button key={cfg.key} className="pl3-tab" onClick={()=>switchTab(cfg.key)}
                    style={{ background:active?`${cfg.accent}14`:"transparent", border:`1px solid ${active?cfg.border:"transparent"}`, color:active?cfg.accent:"rgba(180,180,200,0.3)", boxShadow:active?`0 2px 16px ${cfg.glowSoft}`:"none" }}>
                    <div className="pl3-tab-sh" />
                    <Icon size={14} />{cfg.fullLabel}
                  </button>
                );
              })}
            </div>

            {/* Form */}
            <AnimatePresence mode="wait">
              <motion.form key={activeTab} onSubmit={handleLogin}
                initial={{ opacity:0,y:6 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:-6 }}
                transition={{ duration:0.22 }}
                style={{ display:"flex",flexDirection:"column",gap:14,position:"relative",zIndex:3 }}>

                <AnimatePresence>
                  {loading && (
                    <motion.div initial={{ opacity:0,height:0 }} animate={{ opacity:1,height:"auto" }} exit={{ opacity:0,height:0 }} style={{ overflow:"hidden" }}>
                      <LoadingStages stage={stage} accent={tab.accent} />
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {error && (
                    <motion.div initial={{ opacity:0,height:0,y:-8 }} animate={{ opacity:1,height:"auto",y:0 }} exit={{ opacity:0,height:0 }} style={{ overflow:"hidden" }}>
                      <div style={{ display:"flex",alignItems:"center",gap:9,padding:"10px 13px",borderRadius:11,background:"rgba(239,68,68,0.07)",border:"1px solid rgba(239,68,68,0.18)",color:"#FCA5A5",fontSize:13,fontFamily:"'Outfit',sans-serif" }}>
                        <AlertCircle size={14} style={{ flexShrink:0,color:"#F87171" }} />{error}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Success overlay */}
                <AnimatePresence>
                  {success && (
                    <motion.div initial={{ opacity:0,scale:0.92 }} animate={{ opacity:1,scale:1 }}
                      style={{ position:"absolute",inset:-34,borderRadius:22,background:tab.cardBg,backdropFilter:"blur(14px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,zIndex:20 }}>
                      <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ type:"spring",stiffness:300,damping:18 }}
                        style={{ width:68,height:68,borderRadius:"50%",background:`${tab.accent}18`,border:`2px solid ${tab.accent}`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 50px ${tab.glow}` }}>
                        <ShieldCheck size={30} color={tab.accent} />
                      </motion.div>
                      <div style={{ fontFamily:"'Playfair Display',serif",fontSize:19,fontWeight:800,color:tab.textColor }}>Access Granted!</div>
                      <div style={{ fontFamily:"'Geist Mono',monospace",fontSize:11,color:tab.subText,letterSpacing:"0.08em" }}>Redirecting to dashboard...</div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Email */}
                <div>
                  <label style={{ display:"block",fontSize:11,fontWeight:700,color:tab.labelColor,marginBottom:7,textTransform:"uppercase",letterSpacing:"0.1em" }}>Email Address</label>
                  <div style={{ position:"relative" }}>
                    <Mail size={15} style={{ position:"absolute",left:15,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",zIndex:2,color:focused==="email"?tab.accent:`${tab.accent}44`,transition:"color 0.25s" }} />
                    <input type="email" className="pl3-inp" placeholder={tab.placeholder} value={email} onChange={e=>setEmail(e.target.value)} onFocus={()=>setFocused("email")} onBlur={()=>setFocused("")}
                      style={{ background:focused==="email"?tab.inputFocus:tab.inputBg,border:`1.5px solid ${focused==="email"?`${tab.accent}66`:tab.border}`,boxShadow:focused==="email"?`0 0 0 4px ${tab.glowSoft},0 4px 20px rgba(0,0,0,0.2)`:"0 2px 8px rgba(0,0,0,0.15)",caretColor:tab.accent,color:tab.textColor }} required />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label style={{ display:"block",fontSize:11,fontWeight:700,color:tab.labelColor,marginBottom:7,textTransform:"uppercase",letterSpacing:"0.1em" }}>Password</label>
                  <div style={{ position:"relative" }}>
                    <Lock size={15} style={{ position:"absolute",left:15,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",zIndex:2,color:focused==="pass"?tab.accent:`${tab.accent}44`,transition:"color 0.25s" }} />
                    <input type={showPw?"text":"password"} className="pl3-inp" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} onFocus={()=>setFocused("pass")} onBlur={()=>setFocused("")}
                      style={{ background:focused==="pass"?tab.inputFocus:tab.inputBg,border:`1.5px solid ${focused==="pass"?`${tab.accent}66`:tab.border}`,boxShadow:focused==="pass"?`0 0 0 4px ${tab.glowSoft}`:"0 2px 8px rgba(0,0,0,0.15)",caretColor:tab.accent,color:tab.textColor,paddingRight:48 }} required />
                    <button type="button" onClick={()=>setShowPw(!showPw)}
                      style={{ position:"absolute",right:13,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:`${tab.accent}55`,padding:4,borderRadius:7,display:"flex",transition:"color 0.2s" }}
                      onMouseEnter={e=>e.currentTarget.style.color=tab.accent} onMouseLeave={e=>e.currentTarget.style.color=`${tab.accent}55`}>
                      {showPw?<EyeOff size={15}/>:<Eye size={15}/>}
                    </button>
                  </div>
                </div>

                {/* Staff */}
                <label className="pl3-staff" style={{ background:isStaff?tab.dimBg:"rgba(255,255,255,0.02)",border:`1px solid ${isStaff?tab.border:"rgba(255,255,255,0.07)"}`,boxShadow:isStaff?`0 0 16px ${tab.glowSoft}`:"none" }}>
                  <input type="checkbox" checked={isStaff} onChange={e=>setIsStaff(e.target.checked)} style={{ accentColor:tab.accent,width:14,height:14 }} />
                  <Users size={13} style={{ color:isStaff?tab.accent:"rgba(200,200,220,0.3)",transition:"color 0.22s" }} />
                  <span style={{ fontSize:13,fontWeight:500,color:isStaff?tab.textColor:"rgba(200,200,220,0.38)",transition:"color 0.22s" }}>{t("iAmStaffMember")}</span>
                </label>

                {/* Remember + Forgot */}
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap" }}>
                  <label style={{ display:"flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:12.5,color:"rgba(180,180,210,0.4)",fontWeight:500 }}>
                    <input type="checkbox" style={{ accentColor:tab.accent }} />{t("rememberMe")}
                  </label>
                  <a href="#" style={{ fontSize:12.5,fontWeight:600,color:tab.linkColor,textDecoration:"none",transition:"opacity 0.2s" }}
                    onMouseEnter={e=>e.currentTarget.style.opacity="0.75"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
                    {t("forgotPassword")}
                  </a>
                </div>

                {/* Submit */}
                <motion.button type="submit" className="pl3-btn" disabled={loading||success} whileTap={{ scale:0.98 }}
                  style={{ background:tab.gradient,boxShadow:`0 6px 28px ${tab.shadow},inset 0 1px 0 rgba(255,255,255,0.12)`,marginTop:2 }}>
                  <div className="pl3-btn-sh" />
                  <AnimatePresence mode="wait">
                    {loading?(
                      <motion.span key="load" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} style={{ display:"flex",alignItems:"center",gap:9 }}>
                        <div className="pl3-spin"/>Signing in...
                      </motion.span>
                    ):(
                      <motion.span key="idle" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} style={{ display:"flex",alignItems:"center",gap:9 }}>
                        Sign in as {tab.fullLabel}{isStaff?" Staff":""}<ArrowRight size={15}/>
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>

                <div style={{ height:1,background:tab.border,margin:"2px 0 0" }} />
                <p style={{ textAlign:"center",fontSize:12,color:`${tab.accent}44`,margin:0 }}>
                  {t("needHelpAccessingAccount")}{" "}<a href="mailto:support@1401.com" style={{ color:tab.linkColor,textDecoration:"none",fontWeight:600 }}>support@1401.com</a>
                </p>
              </motion.form>
            </AnimatePresence>
          </div>

          <p style={{ textAlign:"center",fontFamily:"'Geist Mono',monospace",fontSize:10.5,color:"rgba(100,120,160,0.28)",marginTop:18,letterSpacing:"0.04em" }}>
            ACCESS RESTRICTED · © {new Date().getFullYear()} 1401 · All rights reserved
          </p>
        </motion.div>
      </div>
    </div>
  );
}